import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { FirebaseError } from 'firebase/app';
import {
  GoogleAuthProvider,
  linkWithCredential,
  onAuthStateChanged,
  onIdTokenChanged,
  sendPasswordResetEmail,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as fbSignOut,
  type AuthCredential,
  type User,
} from 'firebase/auth';

import { auth } from './firebase.js';
import { agentPushClient } from './push/client.js';
import { googleErrorKey } from './login-errors.js';
import { logger } from './logger.js';
import { watchOwnClaimsWatermark } from './repositories/user-claims-repo.js';

/**
 * AuthProvider — singleton for the agent PWA.
 *
 * Two ways in, both landing in the SAME `onAuthStateChanged` /
 * `onIdTokenChanged` flow (no separate routing branch):
 *
 *   1. The one-time cross-origin `#signInToken` handoff (ADR-0015) that drops a
 *      brand-new agent straight in right after marketing
 *      `/become-an-agent/signup` mints a custom token via `agentBootstrap`.
 *      PRESERVED untouched below.
 *   2. The RETURNING-user login: once that first Firebase session ends the agent
 *      would otherwise dead-end signed-out with no way back. `agentBootstrap`
 *      creates the agent Auth user EMAIL-only (`createUser({ email })`, password
 *      set via a reset link) — so the returning-agent path is email + password +
 *      "Continue with Google" + password reset, exactly like the operator
 *      dashboard (the docs' "phone OTP primary" line is aspirational drift;
 *      the CODE is email — see apps/agent-pwa/CLAUDE.md).
 *
 * The Cloud Function `claimsSyncAgent` sets these custom claims on the user when
 * their `agents/{agentId}` doc transitions to `status: 'active'`:
 *   { agent: true, agentId: '<docId>', agentPlatformBrandId: '<brandId>' }
 * The provider exposes those via `claims`; routes use them to gate access.
 */

/**
 * Outcome of a "Continue with Google" attempt. `link-with-password` is the
 * account-model resolution: the agent already has an email+password account for
 * this Google email (the account `agentBootstrap` created), so Google can't
 * fork a second one — the caller collects the password once and links the
 * Google credential onto the existing account (`linkGoogleAfterPassword`).
 */
export type GoogleSignInOutcome =
  | { readonly status: 'success' }
  | {
      readonly status: 'link-with-password';
      readonly email: string;
      readonly pendingCredential: AuthCredential;
    }
  | { readonly status: 'error'; readonly errorKey: string | null };

export interface AuthState {
  readonly user: User | null;
  readonly claims: Record<string, unknown> | null;
  readonly loading: boolean;
  readonly signOut: () => Promise<void>;
  /**
   * Force a token refresh so a server-side claims change lands immediately.
   * A UX accelerator, not the security boundary — `firestore.rules` refuses a
   * stale token regardless of what any client does (G2/ADR-0007).
   */
  readonly forceRefreshClaims: () => Promise<void>;
  /**
   * Email+password sign-in for returning agents. Resolves on success; rejects
   * with the raw `FirebaseError` so the caller can map `error.code` → human
   * copy via `loginErrorKey`.
   */
  readonly signInWithEmail: (email: string, password: string) => Promise<void>;
  /** Send a password-reset (a.k.a. first-time set-password) email. Rejects with the raw `FirebaseError`. */
  readonly sendPasswordReset: (email: string) => Promise<void>;
  /**
   * "Continue with Google" (primary, one-tap sign-in). Never rejects — returns
   * a discriminated {@link GoogleSignInOutcome} so the caller can drive the
   * popup-cancel / config-error / account-link UI without touching
   * `firebase/auth` itself.
   */
  readonly signInWithGoogle: () => Promise<GoogleSignInOutcome>;
  /**
   * Finish the account link: sign in with the existing password, then attach
   * the pending Google credential so future Google taps just work. Rejects with
   * the raw `FirebaseError` (wrong password → caller maps via `loginErrorKey`).
   */
  readonly linkGoogleAfterPassword: (
    email: string,
    password: string,
    pendingCredential: AuthCredential,
  ) => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { readonly children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [claims, setClaims] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);

  // Consume the cross-origin custom-token handoff exactly once.
  useEffect(() => {
    const fragment = window.location.hash;
    if (!fragment.includes('signInToken=')) return;

    const params = new URLSearchParams(fragment.slice(1));
    const token = params.get('signInToken');
    if (!token) return;

    const { pathname, search } = window.location;
    window.history.replaceState({}, '', pathname + search);

    signInWithCustomToken(auth, token).catch((err: unknown) => {
      logger.error('signInWithCustomToken failed', { error: String(err) });
    });
  }, []);

  const claimsRef = useRef<Record<string, unknown> | null>(null);
  useEffect(() => {
    claimsRef.current = claims;
  }, [claims]);

  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
    const unsubToken = onIdTokenChanged(auth, async (u) => {
      if (!u) {
        setClaims(null);
        return;
      }
      try {
        const result = await u.getIdTokenResult();
        setClaims(result.claims);
      } catch (e) {
        logger.error('getIdTokenResult failed', { error: String(e) });
      }
    });
    return () => {
      unsubAuth();
      unsubToken();
    };
  }, []);

  // G2 — watch our own revocation watermark; see the dashboard twin for the
  // full reasoning. An agent whose status leaves `active` loses their claims
  // server-side, and this is what makes the app notice in seconds.
  useEffect(() => {
    if (!user) return;
    let lastRefresh = 0;
    const unsub = watchOwnClaimsWatermark(user.uid, (revokedAt) => {
      if (revokedAt === null) return;
      const stamped = claimsRef.current?.['claimsRefreshedAt'];
      const current = typeof stamped === 'number' ? stamped : 0;
      if (current >= revokedAt) return;
      const now = Date.now();
      if (now - lastRefresh < 5000) return;
      lastRefresh = now;
      user.getIdToken(true).catch((e: unknown) => {
        logger.error('forced claims refresh failed', { error: String(e) });
      });
    });
    return unsub;
  }, [user]);

  const value: AuthState = {
    user,
    forceRefreshClaims: async () => {
      if (!user) return;
      await user.getIdToken(true);
    },
    claims,
    loading,
    // Push hygiene first, while the outgoing user can still delete their own
    // fcmTokens row — otherwise this device keeps getting their commission and
    // payout pings after sign-out. cleanupForSignOut never throws.
    signOut: async () => {
      const outgoingUid = auth.currentUser?.uid;
      if (outgoingUid) await agentPushClient.cleanupForSignOut(outgoingUid);
      await fbSignOut(auth);
    },
    signInWithEmail: (email, password) =>
      signInWithEmailAndPassword(auth, email, password).then(() => undefined),
    sendPasswordReset: (email) => sendPasswordResetEmail(auth, email),
    signInWithGoogle: async () => {
      try {
        await signInWithPopup(auth, new GoogleAuthProvider());
        return { status: 'success' };
      } catch (err) {
        if (err instanceof FirebaseError) {
          // The email already belongs to a password (agent) account →
          // don't fail; hand the caller the pending Google credential + email
          // so it can collect the password once and link (not fork a 2nd acct).
          if (err.code === 'auth/account-exists-with-different-credential') {
            const pendingCredential = GoogleAuthProvider.credentialFromError(err);
            const emailRaw = err.customData?.['email'];
            const email = typeof emailRaw === 'string' ? emailRaw : null;
            if (pendingCredential && email) {
              return { status: 'link-with-password', email, pendingCredential };
            }
          }
          logger.warn('google sign-in failed', { code: err.code });
        }
        return { status: 'error', errorKey: googleErrorKey(err) };
      }
    },
    // Sign in FIRST (proves the password), THEN attach Google. Both awaits run
    // fully in order; the sign-in flips auth state mid-flow, so the caller
    // (LinkPanel) guards its post-await setState against unmount.
    linkGoogleAfterPassword: async (email, password, pendingCredential) => {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      await linkWithCredential(cred.user, pendingCredential);
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(AuthContext);
  if (!v) throw new Error('useAuth must be used inside <AuthProvider>');
  return v;
}

/**
 * Convenience: returns the agent id if the signed-in user has the
 * `agent: true` + `agentId: string` claims; otherwise null. Pages use
 * this directly so they don't have to defensively cast the claims bag.
 */
export function useAgentId(): string | null {
  const { claims } = useAuth();
  if (!claims) return null;
  const isAgent = claims['agent'] === true;
  const agentId = claims['agentId'];
  if (!isAgent || typeof agentId !== 'string' || agentId.length === 0) return null;
  return agentId;
}

/**
 * Sprint 85 — same shape as `useAgentId` for the recruiter persona.
 * A signed-in user can carry one or both of these claims (recruiters
 * who are also agents themselves is the common case). Routes consume
 * the relevant id directly; the shell decides which nav sections are
 * visible based on which ids resolve.
 *
 * Set by `approveRecruiterApplication` (Sprint 82) via
 * `adminAuth.setCustomUserClaims`: `{ recruiter: true, recruiterId, recruiterPlatformBrandId }`.
 */
export function useRecruiterId(): string | null {
  const { claims } = useAuth();
  if (!claims) return null;
  const isRecruiter = claims['recruiter'] === true;
  const recruiterId = claims['recruiterId'];
  if (!isRecruiter || typeof recruiterId !== 'string' || recruiterId.length === 0) return null;
  return recruiterId;
}
